import { computed, type MaybeRefOrGetter, nextTick, type Ref, ref, toValue } from "vue"
import { entityName as nameOf, type Field } from "@auger/core/fields"

// Item key — what getItemKey returns. Always a string: the DOM keys cells by
// `data-item-key` (a string), so a string-keyed selection model round-trips
// through the DOM with no coercion. The default getItemKey stringifies a numeric
// `id`; a caller's own string keys pass through unchanged.
export type ItemKey = string

export type SortDirection = "asc" | "desc"

export interface SortKey {
  key: string
  direction: SortDirection
}

// One grouping level. Unlike SortKey, direction is optional: when unset the
// group order falls back to the field's configured order (enum schema order,
// boolean [true, false], etc.) rather than being sorted.
export interface GroupKey {
  key: string
  direction?: SortDirection
}

export interface FieldGroupDescriptor {
  key: string
  label: string
  order?: number
}

export interface GroupedFieldsBucket extends FieldGroupDescriptor {
  fields: Field[]
  // Always set by the groupedFields builder (defaults the descriptor's
  // optional `order` to 0), so it's required on the materialized bucket.
  order: number
}

// One entry in the flattened groupedItems output. Either a data row or a
// header for an expanded group. Discriminator: `type`.
export interface CollectionRow<T> {
  type: "row"
  item: T
  index: number
  level?: number
  path?: string
}

export interface CollectionGroup<T> {
  type: "group"
  level: number
  key: string
  value: unknown // enum → string, link → record, default → raw; null = no-value bucket
  label?: string // overrides field-derived text; set for the no-match bucket
  field: Field | null
  path: string
  count: number
  isExpanded: boolean
  firstItem: T | undefined // undefined when the group is empty
  // Every item under this group, including while it is collapsed — a collapsed
  // group emits no rows, so this is the only way to read its contents. A
  // nested group's members appear here and again on each of its sub-groups.
  items: T[]
}

export type GroupedItem<T> = CollectionRow<T> | CollectionGroup<T>

// `fields` accepts ref, computed, getter, or eager array — same flexibility
// useList/useRecord expose for their primary reactive inputs. Other options
// are eager values resolved at composable-init time.
export interface UseCollectionOptions<T> {
  fields?: MaybeRefOrGetter<Field[]>
  getItemKey?: (item: T) => ItemKey
  getItemPath?: (item: T) => string[] | null | undefined
  /** Bucket null-key items into an "Uncategorized" group (default). False emits
   *  them as loose rows at the current level — for folder-style path grouping. */
  groupNullValues?: boolean
  getFieldGroup?: (field: Field) => FieldGroupDescriptor | null
  /** Marks an item hidden. Hidden items stay out until `showHidden` is on.
   *  Omitted, nothing is hidden. */
  isHidden?: (item: T) => boolean
}

/**
 * Get nested value from object using dot notation path.
 * Handles array access like "items[0].name". Returns undefined on miss.
 */
export function getNestedValue(obj: unknown, path: string): unknown {
  if (!obj || !path) return undefined
  return path.split(".").reduce<unknown>((acc, key) => {
    if (acc == null) return undefined
    const cur = acc as Record<string, unknown>
    if (key.includes("[")) {
      const [arrayKey, indexStr] = key.split("[")
      if (!arrayKey || !indexStr) return undefined
      const index = parseInt(indexStr.replace("]", ""))
      const arr = cur[arrayKey]
      return Array.isArray(arr) ? arr[index] : undefined
    }
    return cur[key]
  }, obj)
}

/**
 * Composable for data organization: filtering, sorting, and grouping.
 * Presentation-agnostic — can be consumed by useGrid, useBoard, useGallery, etc.
 *
 * @example
 * const collection = useCollection(records, { fields: () => allFields, getItemKey: r => r.id })
 *
 * @example // Folder-style path grouping
 * const collection = useCollection(assets, {
 *   fields: () => assetFields,
 *   getItemPath: (asset) => asset.path?.split("/"),
 * })
 */
export function useCollection<T extends Record<string, unknown> = Record<string, unknown>>(
  items: Ref<T[]>,
  options: UseCollectionOptions<T> = {}
) {
  const {
    fields: fieldsOption,
    // Default reads `item.id`. If T doesn't carry one, the cast through `?`
    // silently produces `undefined` which would collapse every keyless item
    // under one phantom key. Fail loudly instead — callers without `id` must
    // supply getItemKey.
    getItemKey = (item: T): ItemKey => {
      const id = (item as { id?: string | number }).id
      if (id == null) {
        throw new Error(
          "[useCollection] Default getItemKey requires items to have a non-null `id`. " +
            "Pass `getItemKey` to derive a key from this item shape."
        )
      }
      return String(id)
    },
    getItemPath,
    groupNullValues = true,
    getFieldGroup,
    isHidden,
  } = options

  // --- Hidden State ---
  const showHidden = ref(false)

  function toggleShowHidden() {
    showHidden.value = !showHidden.value
  }

  const userFields = computed<Field[]>(() => toValue(fieldsOption) ?? [])

  // --- Path Grouping (folder-like navigation) ---

  // Calculate max path depth across all items
  const maxPathDepth = computed(() => {
    if (!getItemPath) return 0
    let max = 0
    for (const item of items.value) {
      const segments = getItemPath(item)
      if (segments) max = Math.max(max, segments.length)
    }
    return max
  })

  // Generate hidden path segment fields for recursive grouping
  const pathSegmentFields = computed<Field[]>(() => {
    if (!getItemPath || maxPathDepth.value === 0) return []
    const pathFields: Field[] = []
    for (let i = 0; i < maxPathDepth.value; i++) {
      const value = (item: unknown) => {
        const segments = getItemPath(item as T)
        return segments?.[i] || null
      }
      pathFields.push({
        key: `_path_${i}`,
        label: i === 0 ? "Folder" : `Folder ${i + 1}`,
        type: "string",
        icon: "folder", // Synthetic grouping column: owns its icon (not type-derived).
        isHidden: true, // Hidden from all dropdowns - use togglePathGrouping() instead
        value,
        // No separate stored id (grouping-only segment), so rawValue === value.
        rawValue: value,
      })
    }
    return pathFields
  })

  // Combine user fields with auto-generated path fields
  // allFields includes ALL fields (including hidden) - presentation layers filter as needed
  const allFields = computed<Field[]>(() => [...userFields.value, ...pathSegmentFields.value])

  // --- Field Grouping ---

  const groupedFields = computed<GroupedFieldsBucket[] | null>(() => {
    if (!getFieldGroup) return null

    const groups = new Map<string, GroupedFieldsBucket>()

    for (const field of allFields.value) {
      const group = getFieldGroup(field)
      if (!group) continue

      const { key, label, order = 0 } = group

      if (!groups.has(key)) {
        groups.set(key, { key, label, order, fields: [] })
      }
      groups.get(key)!.fields.push(field)
    }

    return Array.from(groups.values()).sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order
      return a.label.localeCompare(b.label)
    })
  })

  // --- Filtering State ---
  const filterFn = ref<((item: T) => boolean) | null>(null)

  // --- Sorting State ---
  const sortKeys = ref<SortKey[]>([]) // Multi-column sort: [{ key, direction }]

  // --- Grouping State ---
  // Multi-level grouping: each level carries its own optional sort direction.
  const groupKeys = ref<GroupKey[]>([])
  const expandedGroups = ref<Set<string>>(new Set())

  function groupDirectionFor(key: string): SortDirection | undefined {
    return groupKeys.value.find((g) => g.key === key)?.direction
  }

  // --- Sorting Helpers ---

  function setSortBy(key: string | null | undefined, direction: SortDirection = "asc") {
    sortKeys.value = key ? [{ key, direction }] : []
  }

  function toggleSort(key: string) {
    const existing = sortKeys.value.find((s) => s.key === key)
    if (existing) {
      const newDirection: SortDirection = existing.direction === "asc" ? "desc" : "asc"
      sortKeys.value = sortKeys.value.map((s) =>
        s.key === key ? { ...s, direction: newDirection } : s
      )
    } else {
      sortKeys.value = [{ key, direction: "asc" }]
    }
  }

  /**
   * Clear all sorting
   */
  function clearSort() {
    sortKeys.value = []
  }

  // --- Filtering Helpers ---

  function setFilter(fn: ((item: T) => boolean) | null) {
    filterFn.value = fn
  }

  function clearFilter() {
    filterFn.value = null
  }

  // --- Grouping Helpers ---

  /**
   * A group's path: each level's key plus its stable identity (see emitGroup),
   * so `expandedGroups` keys survive refetches that rebuild the group objects.
   */
  function buildGroupPath(groupValues: Array<{ key: string; identity: unknown }>): string {
    return groupValues.map(({ key, identity }) => `${key}:${JSON.stringify(identity)}`).join("|")
  }

  function isGroupExpanded(path: string): boolean {
    return expandedGroups.value.has(path)
  }

  /** Open one group, idempotently — `toggleGroup` would shut an open one. */
  function expandGroup(path: string) {
    if (expandedGroups.value.has(path)) return
    expandedGroups.value.add(path)
    expandedGroups.value = new Set(expandedGroups.value)
  }

  function toggleGroup(path: string) {
    if (expandedGroups.value.has(path)) {
      expandedGroups.value.delete(path)
    } else {
      expandedGroups.value.add(path)
    }
    // Trigger reactivity
    expandedGroups.value = new Set(expandedGroups.value)
  }

  /**
   * Set the groupBy keys (column keys to group by, in order).
   * Accepts either an array of strings or an array of {key, direction} objects.
   */
  function setGroupBy(keys: Array<string | GroupKey>) {
    groupKeys.value = (keys || []).map((entry) =>
      typeof entry === "string" ? { key: entry, direction: undefined } : { ...entry }
    )
    // Auto-expand all groups after changing grouping
    nextTick(() => expandAllGroups())
  }

  /**
   * Add a grouping level, with an optional initial sort direction.
   */
  function addGroupBy(key: string, direction?: SortDirection) {
    if (!groupKeys.value.some((g) => g.key === key)) {
      groupKeys.value = [...groupKeys.value, { key, direction }]
      // Auto-expand all groups when a new group is added
      nextTick(() => expandAllGroups())
    }
  }

  function removeGroupBy(key: string) {
    groupKeys.value = groupKeys.value.filter((g) => g.key !== key)
  }

  function clearGroupBy() {
    groupKeys.value = []
    expandedGroups.value = new Set()
  }

  // --- Path Grouping Helpers ---

  const isPathGrouped = computed(() => !!getItemPath && groupKeys.value[0]?.key === "_path_0")

  /**
   * Toggle folder view on/off.
   * When enabled, groups by all path segments for recursive folder navigation.
   * Requires getItemPath option to be provided.
   */
  function togglePathGrouping() {
    if (!getItemPath) return
    if (isPathGrouped.value) clearGroupBy()
    else setGroupBy(Array.from({ length: maxPathDepth.value }, (_, i) => `_path_${i}`))
  }

  /** Reset collection state (sort, grouping, expanded groups). */
  function resetState() {
    clearSort()
    clearGroupBy()
  }

  // --- Computed: Filtered Items ---

  const filteredItems = computed<T[]>(() => {
    let result = items.value
    if (isHidden && !showHidden.value) {
      result = result.filter((item) => !isHidden(item))
    }
    const fn = filterFn.value
    if (fn) result = result.filter(fn)
    return result
  })

  // Also true when the hidden-items gate is actually excluding
  // something — it narrows filteredItems just as much as a caller's own
  // filterFn does, so "showing N of M" style UI should account for it too.
  const isFiltered = computed(
    () => filterFn.value !== null || (!!isHidden && !showHidden.value && items.value.some(isHidden))
  )

  // --- Computed: Sorted Items ---

  /**
   * Default comparator for sorting values.
   * Handles strings (case-insensitive), numbers, booleans, dates, and objects with .name
   */
  function defaultCompare(a: unknown, b: unknown): number {
    if (a == null && b == null) return 0
    if (a == null) return 1
    if (b == null) return -1

    // Objects with name: extract name for comparison
    let aVal: unknown = a
    let bVal: unknown = b

    // Array of objects/values: extract the first element for comparison
    if (Array.isArray(aVal)) {
      aVal = aVal[0] ?? null
    }
    if (Array.isArray(bVal)) {
      bVal = bVal[0] ?? null
    }

    if (aVal == null && bVal == null) return 0
    if (aVal == null) return 1
    if (bVal == null) return -1

    aVal = nameOf(aVal)
    bVal = nameOf(bVal)

    if (typeof aVal === "boolean" && typeof bVal === "boolean") {
      return aVal === bVal ? 0 : aVal ? -1 : 1
    }

    if (typeof aVal === "number" && typeof bVal === "number") {
      return aVal - bVal
    }

    if (isDateLike(aVal) && isDateLike(bVal)) {
      return new Date(aVal as string | Date).getTime() - new Date(bVal as string | Date).getTime()
    }

    if (typeof aVal === "string" && typeof bVal === "string") {
      return aVal.toLowerCase().localeCompare(bVal.toLowerCase())
    }

    // Fallback for mixed types
    warnMixedTypes(aVal, bVal)
    return (aVal as number) > (bVal as number) ? 1 : (aVal as number) < (bVal as number) ? -1 : 0
  }

  // Mixed types have no meaningful order. Say so once per type pair in dev, so a
  // field that needs its own `sort` shows up instead of sorting arbitrarily.
  const warnedTypePairs = new Set<string>()
  function warnMixedTypes(a: unknown, b: unknown) {
    if (!import.meta.env.DEV) return
    const pair = [typeof a, typeof b].sort().join("/")
    if (warnedTypePairs.has(pair)) return
    warnedTypePairs.add(pair)
    console.warn(`useCollection: sorting mixed types (${pair}); give the field a \`sort\``)
  }

  function isDateLike(val: unknown): boolean {
    if (val instanceof Date) return true
    if (typeof val !== "string") return false
    // ISO date strings like "2024-01-15" or "2024-01-15T10:30:00Z"
    return /^\d{4}-\d{2}-\d{2}/.test(val)
  }

  const sortedItems = computed<T[]>(() => {
    if (sortKeys.value.length === 0) {
      return filteredItems.value
    }

    return filteredItems.value.toSorted((a, b) => {
      for (const sortItem of sortKeys.value) {
        const field = allFields.value.find((f) => f.key === sortItem.key)
        const direction = sortItem.direction === "asc" ? 1 : -1

        // The field's own ordering (its type's `sort`) compares the raw values;
        // defaultCompare orders a field that is sortable without one.
        const valA = field?.value ? field.value(a) : getNestedValue(a, sortItem.key)
        const valB = field?.value ? field.value(b) : getNestedValue(b, sortItem.key)

        const result =
          (field?.sort ? field.sort(valA, valB, field) : defaultCompare(valA, valB)) * direction

        if (result !== 0) return result
      }
      return 0
    })
  })

  // --- Computed: Grouped Items ---

  /**
   * Transform items into a flat array with group headers.
   * Each entry is either a row ({ type: "row", item, index }) or a group
   * header ({ type: "group", level, key, ... }).
   */
  const groupedItems = computed<GroupedItem<T>[]>(() => {
    const data = sortedItems.value
    const keys = groupKeys.value.map((g) => g.key)

    if (keys.length === 0) {
      const len = data.length
      const result = new Array<GroupedItem<T>>(len)
      for (let i = 0; i < len; i++) {
        result[i] = { type: "row", item: data[i]!, index: i }
      }
      return result
    }

    const result: GroupedItem<T>[] = []

    function getFieldValue(item: T, field: Field | undefined, key: string): unknown {
      return field?.value ? field.value(item) : getNestedValue(item, key)
    }

    function emitGroup(
      group: unknown,
      groupItems: T[],
      level: number,
      parentPath: Array<{ key: string; identity: unknown }>,
      label?: string
    ) {
      const key = keys[level]
      if (!key) {
        console.warn(
          `[useCollection] emitGroup called with level=${level} but only ${keys.length} group keys`
        )
        return
      }
      const field = allFields.value.find((f) => f.key === key)
      // A group is a scalar (select/boolean) or an entity object (record, tag,
      // type, …); its stable path identity is the entity's key — `id`, or
      // `name`/`slug` for keyless entities like a type — else the value itself.
      // Optional chaining no-ops on scalars/null, so they fall through to `group`.
      const g = group as { id?: unknown; name?: unknown; slug?: unknown } | null
      const identity = g?.id ?? g?.name ?? g?.slug ?? group
      const currentPath = [...parentPath, { key, identity }]
      const pathString = buildGroupPath(currentPath)

      result.push({
        type: "group",
        level,
        key,
        value: group,
        field: field ? ({ ...field, isEditable: false } as Field) : null,
        path: pathString,
        count: groupItems.length,
        isExpanded: isGroupExpanded(pathString),
        firstItem: groupItems[0],
        items: groupItems,
        label,
      })

      if (isGroupExpanded(pathString)) {
        if (level < keys.length - 1) {
          groupRecursively(groupItems, level + 1, currentPath)
        } else {
          for (const item of groupItems) {
            result.push({
              type: "row",
              item,
              index: data.indexOf(item),
              level: level + 1,
              // Include path for unique keys when items appear in multiple groups (e.g., tags)
              path: `${pathString}.${getItemKey(item)}`,
            })
          }
        }
      }
    }

    function groupRecursively(
      itemsToGroup: T[],
      level: number,
      parentPath: Array<{ key: string; identity: unknown }> = []
    ) {
      const key = keys[level]
      if (!key) {
        console.warn(
          `[useCollection] groupRecursively called with level=${level} but only ${keys.length} group keys`
        )
        return
      }
      const field = allFields.value.find((f) => f.key === key)

      // Type-defined grouping (number, boolean, enum, tags, etc.).
      // enrichField copies allGroups and matchesGroup from FIELD_TYPES.
      if (field?.allGroups && field.matchesGroup) {
        const values = itemsToGroup.map((item) => getFieldValue(item, field, key))

        let groups: unknown[]
        if (typeof field.allGroups === "function") {
          groups = [...field.allGroups(values, field)]
        } else {
          groups = Array.isArray(field.allGroups) ? [...field.allGroups] : []
        }

        // Only sort groups when the user has explicitly set a direction.
        // Without an explicit direction, preserve the configured order from
        // field.values / field.allGroups (e.g., enum schema order, boolean [true, false]).
        const explicitDir = groupDirectionFor(key)
        if (explicitDir) {
          const typeDir = explicitDir === "desc" ? -1 : 1
          groups.sort((a, b) => typeDir * String(nameOf(a)).localeCompare(String(nameOf(b))))
        }

        const { matchesGroup } = field
        const matched = new Set<T>()
        for (const group of groups) {
          const groupItems = itemsToGroup.filter((item) => {
            const value = getFieldValue(item, field, key)
            return matchesGroup(value, group)
          })

          // Always emit groups when using allGroups (e.g., stages, tags)
          // This ensures empty groups are visible in kanban/grid views
          emitGroup(group, groupItems, level, parentPath)
          for (const item of groupItems) matched.add(item)
        }

        // Catch items that didn't match any predefined group. `null` is the
        // no-value sentinel; the label spells out which field is missing.
        const unmatched = itemsToGroup.filter((item) => !matched.has(item))
        if (unmatched.length > 0) {
          emitGroup(null, unmatched, level, parentPath, `No ${field.label || key}`)
        }
        return
      }

      // Default grouping by exact value match
      const groups = new Map<string, { value: unknown; items: T[] }>()
      const nullItems: T[] = []

      for (const item of itemsToGroup) {
        const value = getFieldValue(item, field, key)

        if (value == null) {
          nullItems.push(item)
          continue
        }

        const valueKey = JSON.stringify(value)

        if (!groups.has(valueKey)) {
          groups.set(valueKey, { value, items: [] })
        }
        groups.get(valueKey)!.items.push(item)
      }

      // Sort groups alphabetically, respecting per-group sort direction
      const dir = groupDirectionFor(key) === "desc" ? -1 : 1
      const sortedGroups = [...groups.values()].sort(
        (a, b) => dir * String(nameOf(a.value)).localeCompare(String(nameOf(b.value)))
      )

      for (const group of sortedGroups) {
        emitGroup(group.value, group.items, level, parentPath)
      }

      // Handle null items based on groupNullValues option
      if (nullItems.length > 0) {
        if (groupNullValues) {
          emitGroup(null, nullItems, level, parentPath)
        } else {
          // Emit as ungrouped rows at current level (e.g., files in a folder)
          for (const item of nullItems) {
            result.push({
              type: "row",
              item,
              index: data.indexOf(item),
              level,
            })
          }
        }
      }
    }

    groupRecursively(data, 0)
    return result
  })

  /**
   * Expand all groups. Iterates because each expansion reveals subgroups;
   * converges in O(nesting-depth) iterations (typically 2-3).
   */
  function expandAllGroups() {
    // Snapshot initial state so we can roll back on iteration failure. Without this,
    // a corrupted item halfway through groupedItems could leave the user with a
    // partial expansion state and no indication of what happened.
    const initialExpanded = new Set(expandedGroups.value)
    let prevSize = -1
    let iterations = 0
    while (expandedGroups.value.size !== prevSize && iterations < 10) {
      prevSize = expandedGroups.value.size
      iterations++
      const allPaths = new Set(expandedGroups.value)
      try {
        for (const item of groupedItems.value) {
          if (item.type === "group") {
            allPaths.add(item.path)
          }
        }
      } catch (err) {
        console.error(
          "[collection] expandAllGroups: error accessing groupedItems, rolling back:",
          err
        )
        expandedGroups.value = initialExpanded
        return
      }
      expandedGroups.value = allPaths
    }
  }

  function collapseAllGroups() {
    expandedGroups.value = new Set()
  }

  // --- Computed: Grouping ---

  const isGrouped = computed(() => groupKeys.value.length > 0)

  // --- Field Subsets ---

  // Non-hidden fields. The seed for `sortedFields` before any view syncs.
  const filteredFields = computed<Field[]>(() => allFields.value.filter((f) => !f.isHidden))

  // The active view's displayed fields (order + visibility), mirrored here so
  // view-agnostic consumers that need the field axis can track it. Seeded to
  // visible fields; each view writes its `displayFields` via `setSortedFields`.
  const sortedFields = ref<Field[]>(filteredFields.value)

  function setSortedFields(fields: Field[]) {
    sortedFields.value = fields
  }

  /**
   * Fields that can be sorted: those with a `sort`. Hidden fields excluded.
   */
  const sortableFields = computed<Field[]>(() =>
    allFields.value.filter((f) => !f.isHidden && typeof f.sort === "function")
  )

  /**
   * Fields that can be grouped by: those declaring both allGroups and matchesGroup.
   * Hidden fields excluded.
   */
  const groupableFields = computed<Field[]>(() =>
    allFields.value.filter(
      (f) => !f.isHidden && !!f.allGroups && typeof f.matchesGroup === "function"
    )
  )

  /**
   * Fields that can be filtered. Filterable unless explicitly isFilterable: false. Hidden excluded.
   */
  const filterableFields = computed<Field[]>(() =>
    allFields.value.filter((f) => !f.isHidden && f.isFilterable !== false)
  )

  return {
    // --- Source ---
    items,
    allFields,
    sortedFields,
    setSortedFields,
    groupedFields,
    sortableFields,
    groupableFields,
    filterableFields,

    // --- Filtered Output ---
    filteredItems,
    isFiltered,

    // --- Sorted Output ---
    sortedItems,

    // --- Grouped Output ---
    groupedItems,
    isGrouped,

    // --- Filtering State ---
    filterFn,
    showHidden,

    // --- Sorting State ---
    sortKeys,

    // --- Grouping State ---
    groupKeys,
    expandedGroups,

    // --- Filtering Methods ---
    setFilter,
    clearFilter,
    toggleShowHidden,

    // --- Sorting Methods ---
    setSortBy,
    toggleSort,
    clearSort,

    // --- Grouping Methods ---
    setGroupBy,
    addGroupBy,
    removeGroupBy,
    clearGroupBy,
    toggleGroup,
    expandGroup,
    expandAllGroups,
    collapseAllGroups,
    isGroupExpanded,

    // --- Path Grouping (folder navigation) ---
    isPathGrouped,
    togglePathGrouping,

    // --- Reset ---
    resetState,

    // --- Utilities ---
    getItemKey,
    getNestedValue,
  }
}
